import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getOffsetDate } from 'shared/lib/getOffsetDate';
import { useGetJsonParams } from 'shared/lib/useGetJsonParams';
import { AgreementDetail } from 'shared/ui/AgreementDetail/AgreementDetail';
import { PeriodSelectModal } from 'shared/ui/PeriodSelectModal/PeriodSelectModal';
import { ArrowIcon02, PeriodArrowIcon } from 'shared/ui/icon/ArrowIcon';
import { IconLoaderTitleDesc, IconTitleDescButton } from 'shared/ui/onBoarding';
import { ReactComponent as HealthRecordsFetch } from 'styles/images/ic_loading.svg';
import emptyIcon from 'styles/images/lab-list__empty-icon.svg';
import { ReactComponent as WarningIcon } from 'styles/images/onboarding-warning-icon.svg';
import PromoVisualImg from 'styles/images/promo-visual-img.svg';
import TimelineBannerBlue from 'styles/images/timeline-banner-ic-blue.svg';
import TimelineBannerGreen from 'styles/images/timeline-banner-ic-green.svg';
import TimelineBannerRed from 'styles/images/timeline-banner-ic-red.svg';
import TimelineBannerYellow from 'styles/images/timeline-banner-ic-yellow.svg';
import Emergency from 'styles/images/timeline-emergency-icon.svg';
import Inpatient from 'styles/images/timeline-inpatient-icon.svg';
import Outpatient from 'styles/images/timeline-outpatient-icon.svg';
import Surgery from 'styles/images/timeline-surgery-icon.svg';

import { useGetKarenoteTimeline } from '../api';
import { pactTypeClass, pactTypeTitle } from '../const';

const pactTypeImage = {
  I: Inpatient,
  S: Surgery,
  O: Outpatient,
  E: Emergency,
};

const textBanner = [
  {
    className: 'timeline-banner--blue',
    icon: TimelineBannerBlue,
    text: (
      <>
        <span className='timeline-banner__bold'>다른 병원 기록</span>도{' '}
        <span className='timeline-banner__bold'>한 눈에</span> 보고 싶다면?
      </>
    ),
  },
  {
    className: 'timeline-banner--yellow',
    icon: TimelineBannerYellow,
    text: (
      <>
        <span className='timeline-banner__bold'>진료 기록</span>과{' '}
        <span className='timeline-banner__bold'>처방 이력</span>을 확인해 보세요
      </>
    ),
  },
  {
    className: 'timeline-banner--green',
    icon: TimelineBannerGreen,
    text: (
      <>
        <span className='timeline-banner__bold'>진료기록, 한 곳에서</span> 모아볼 수 있어요!
      </>
    ),
  },
  {
    className: 'timeline-banner--red',
    icon: TimelineBannerRed,
    text: (
      <>
        내 <span className='timeline-banner__bold'>건강 정보</span>를{' '}
        <span className='timeline-banner__bold'>쉽고 편리하게</span> 관리하세요
      </>
    ),
  },
];

export const Main = () => {
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const { hspId, ptNm, patientNumber } = useGetJsonParams();

  const [isPeriodOpen, setIsPeriodOpen] = useState(false);
  const [period, setPeriod] = useState({
    key: '1/Y',
    label: '최근 1년',
  });

  const [isRefetching, setIsRefetching] = useState(false);

  const { data, refetch, isFetching } = useGetKarenoteTimeline({
    hspId,
    patientNumber,
    searchStartDate: (() => {
      const [num, unit] = period.key.split('/');
      return getOffsetDate(-Number(num), unit).toISOString().slice(0, 10);
    })(),
    searchEndDate: new Date().toISOString().slice(0, 10),
  });
  useEffect(() => {
    if (!isFetching) setIsRefetching(false);
  }, [isFetching]);

  const banner = useMemo(() => {
    return [...textBanner].sort(() => Math.random() - 0.5);
  }, [data]);

  if (isRefetching)
    return (
      <IconLoaderTitleDesc
        title={
          <>
            {ptNm}님의 정보를
            <br />
            확인하고 있어요
          </>
        }
        icon={<HealthRecordsFetch />}
        desc='잠시만 기다려 주세요.'
      />
    );

  if (!data) return null;

  if (data.result === 1)
    return (
      <div>
        <IconTitleDescButton
          icon={<WarningIcon />}
          title='잠시 문제가 발생했어요'
          desc={
            <>
              병원 진료 이력을 불러오지 못했어요.
              <br />
              다시 시도할까요?
            </>
          }
          buttons={[
            {
              label: '닫기',
              variant: 'secondary',
              // onClick: () => {

              // },
            },
            {
              label: '다시 시도',
              variant: 'primary',
              onClick: () => {
                setIsRefetching(true);
                refetch();
              },
            },
          ]}
        />
      </div>
    );

  if (searchParams.get('terms'))
    return (
      <AgreementDetail
        ccrcGuidId={83}
        hspId={13}
        hspCd={'01'}
        onSubmit={() => {
          // setTermsOpen(false);
          navigate(-1);
        }}
        useData={true}
      />
    );

  return (
    <div className='karenote'>
      <div className='karenote__container'>
        {/* 헤더 */}
        <section className='karenote-header'>
          <div className='karenote-header__title-wrap'>
            <h2 className='karenote-header__title'>
              <span className='karenote-header__user'>{ptNm}</span>님의 케어노트
            </h2>
          </div>
        </section>

        {/* 타임라인 */}
        <section className='timeline'>
          {(() => {
            const bannerGap = 5;
            let cardIndex = 0;
            const totalCardCount = data.timeline.length;

            const years = [];
            data.timeline?.map((datum) => {
              const standardDate = datum.pactEndDate || datum.pactStartDate;
              const year = new Date(standardDate).getFullYear();
              if (!years.includes(year)) years.push(year);
            });
            years.sort((a, b) => b - a);
            if (!years.length)
              return (
                <div className='timeline__year-group'>
                  <div className='timeline__year'>
                    <h3 className='timeline__title timeline__title--year'></h3>
                    <button
                      className={`lab-result__filter ${isPeriodOpen ? 'is-open' : ''}`}
                      onClick={() => setIsPeriodOpen(true)}
                    >
                      <span>{period.label}</span>
                      <PeriodArrowIcon />
                    </button>
                  </div>
                  <div className='timeline__empty'>
                    <img src={emptyIcon} alt='이상 수치 없음' className='timeline__empty-icon' />
                    <p className='timeline__empty-text'>병원 진료 이력이 없어요!</p>
                  </div>
                </div>
              );
            return years.map((year, idx) => {
              return (
                <div className='timeline__year-group' key={year}>
                  <div className='timeline__year'>
                    <h3 className='timeline__title timeline__title--year'>{year}</h3>
                    {/* 최근 dropdown 노출 */}
                    {idx === 0 && (
                      <button
                        className={`lab-result__filter ${isPeriodOpen ? 'is-open' : ''}`}
                        onClick={() => setIsPeriodOpen(true)}
                      >
                        <span>{period.label}</span>
                        <PeriodArrowIcon />
                      </button>
                    )}
                  </div>

                  <ul className='timeline__list'>
                    {(() => {
                      const yearData = data.timeline.filter((datum) => {
                        const standardDate = datum.pactEndDate || datum.pactStartDate;
                        const y = new Date(standardDate).getFullYear();
                        return year === y;
                      });

                      return yearData.map((datum) => {
                        cardIndex++;
                        const tests = [];
                        ['funcTestDescription', 'imgTestDescription', 'patTestDescription', 'spcTestDescription'].map(
                          (key) => {
                            if (datum[key]) tests.push(datum[key]);
                          },
                        );
                        const testIsExist = tests.length;

                        const useCardDetail = testIsExist || datum.pactType !== 'E';

                        return (
                          <li
                            className={`timeline-item timeline-item--${pactTypeClass[datum.pactType]}`}
                            key={datum.pactId}
                            onClick={() => {
                              navigate(
                                '/karenote/pact' +
                                  window.location.search +
                                  `&pactId=${datum.pactId}&pactType=${datum.pactType}&departmentName=${datum.departmentName}&diagnosisName=${datum.diagnosisName}&pactStartDate=${datum.pactStartDate}&pactEndDate=${datum.pactEndDate}&doctorName=${datum.doctorName}`,
                              );
                            }}
                          >
                            <div className='timeline-item__icon'>
                              <img src={pactTypeImage[datum.pactType]} alt='timeline icon' />
                            </div>

                            <div className='timeline-card'>
                              <div className='timeline-card__header'>
                                <span className='timeline-card__date'>
                                  {(() => {
                                    function formatter(dateData) {
                                      const date = new Date(dateData);
                                      const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
                                      return `${date.getMonth() + 1}월 ${date.getDate()}일 ${weekdays[date.getDay()]}요일`;
                                    }
                                    return (
                                      <>
                                        <span>
                                          {formatter(datum.pactStartDate)} {datum.pactEndDate && ' ~ '}
                                        </span>
                                        {datum.pactEndDate && <span>{formatter(datum.pactEndDate)}</span>}
                                      </>
                                    );
                                  })()}
                                </span>

                                <div className='timeline-card__title-wrap'>
                                  <h4 className='timeline-card__title'>
                                    <span className='timeline-card__badge'>
                                      <span>{pactTypeTitle[datum.pactType]}</span>
                                    </span>
                                    <span className='timeline-card__hospital'>{datum.departmentName}</span>
                                  </h4>

                                  <div className='timeline-card__action'>
                                    {/* <span className="icon more" /> */}
                                    <ArrowIcon02 />
                                  </div>
                                </div>

                                {datum.pactType !== 'E' && (
                                  <span className='timeline-card__doctor'>
                                    <span className='text'>{datum.doctorName} 의사</span>
                                  </span>
                                )}

                                {/* 상세 영역 */}
                                {/* {useCardDetail && (
                                  <div className='timeline-card__detail'>
                                    {datum.pactType !== 'E' && (
                                      <div className='timeline-card__section'>
                                        <h4 className='timeline-card__section-title'>진료</h4>
                                        <ul className='timeline-card__content list'>
                                          <li>{datum.diagnosisName}</li>
                                        </ul>
                                      </div>
                                    )}

                                    {!!testIsExist && (
                                      <div className='timeline-card__section'>
                                        <h4 className='timeline-card__section-title'>검사</h4>
                                        <ul className='timeline-card__content box-list'>
                                          {tests.map((test) => (
                                            <li key={test}>
                                              <span className='title'>{test.split(' 외 ')[0]}</span>
                                              <span className='count'>{test.split(' 외 ')[1]}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )} */}
                              </div>
                            </div>
                            {/* 배너 blue, yellow, green, red*/}

                            {((totalCardCount < bannerGap && totalCardCount === cardIndex) ||
                              cardIndex % bannerGap === 0) &&
                              (() => {
                                const bannerIndex =
                                  totalCardCount < bannerGap
                                    ? 0
                                    : Math.floor((cardIndex - 1) / bannerGap) % banner.length;

                                return (
                                  <div
                                    className={`timeline-banner ${banner[bannerIndex].className}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.location.href = 'https://karechat-common.kakaohealthcare.com/welcome';
                                    }}
                                  >
                                    {/* 아이콘 */}
                                    <div className='timeline-banner__icon'>
                                      <img src={banner[bannerIndex].icon} alt='' />
                                    </div>
                                    <div className='timeline-banner__text'>{banner[bannerIndex].text}</div>
                                  </div>
                                );
                              })()}
                          </li>
                        );
                      });
                    })()}
                  </ul>
                </div>
              );
            });
          })()}
        </section>
        {/* 프로모션 카드  */}
        <div className='promo-spacer'></div>
        <section className='promo-card '>
          <div
            className='promo-card-wrap-container red'
            onClick={() => {
              window.location.href = 'https://karechat-common.kakaohealthcare.com/welcome';
            }}
          >
            <div className='promo-card-wrap'>
              <div className='promo__content'>
                <span className='promo__badge'>케어챗 마이데이터</span>
                <h3 className='promo__title'>
                  다른 병원 기록도
                  <br />한 눈에 보고 싶다면?
                </h3>
                <p className='promo__desc'>나의 모든 기록을 한 곳에서!</p>
              </div>
              <div className='promo__image'>
                <img src={PromoVisualImg} alt='' />
              </div>
            </div>
          </div>
        </section>

        {/* 푸터 */}
        <footer className='karenote-footer'>
          <div className='karenote-main-footer__content'>
            <div className='karenote-main-footer__links'>
              <a
                href='#'
                className='karenote-main-footer__link'
                onClick={(e) => {
                  e.preventDefault();
                  navigate(window.location.search + '&terms=true');
                }}
              >
                이용약관
              </a>
              <a href={data.privacyPolicyUrl} className='karenote-main-footer__link bold'>
                개인정보처리방침
              </a>
            </div>

            <p
              className='karenote-main-footer__text'
              onClick={() => {
                navigate(`/karenote/service-cancel?patientNumber=${patientNumber}&hspId=${hspId}`);
              }}
            >
              케어노트 이용을 해지하시겠어요?
            </p>
          </div>
        </footer>

        <PeriodSelectModal
          period={period}
          onSelect={(item) => {
            setPeriod(item);
          }}
          isPeriodOpen={isPeriodOpen}
          setIsPeriodOpen={setIsPeriodOpen}
          options={[
            {
              key: '1/Y',
              label: '최근 1년',
            },
            {
              key: '2/Y',
              label: '최근 2년',
            },
            {
              key: '3/Y',
              label: '최근 3년',
            },
          ]}
        />
      </div>
    </div>
  );
};

